import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { TopNav } from "@/pages/Marketing";
import { SiteFooter } from "@/pages/Legal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  FileText, Award, Mail, LineChart, Table2, FolderLock, ScrollText,
  Download, Eye, Play, ArrowRight, BookOpen, HelpCircle, Video, TrendingUp,
  Scale, Handshake, Target, ExternalLink, CheckCircle2,
} from "lucide-react";

const NAVY = "#174C82";

/* ------------------------------- data ------------------------------- */
const SECTIONS = [
  { id: "documents", label: "Document Library", icon: FileText },
  { id: "why", label: "Why It Matters", icon: BookOpen },
  { id: "videos", label: "Video Guides", icon: Video },
  { id: "faq", label: "FAQ", icon: HelpCircle },
];

const DOCS = [
  { icon: Award, name: "Certificate of Valuation", tiers: "Professional · Institutional · Unlimited",
    desc: "A formal, verifiable certificate stating your enterprise value, range and the five methods used — with a public verification QR code.",
    sample: "/samples/sample-certificate.pdf" },
  { icon: FileText, name: "Professional Valuation Report", tiers: "Professional",
    desc: "10–15 page investor-ready report: executive summary, five-method breakdown, core SaaS metrics (ARR, CAC, LTV, Rule of 40) and an investor letter.",
    sample: "/samples/sample-professional.pdf" },
  { icon: ScrollText, name: "Institutional & Regulatory Pack", tiers: "Institutional · Unlimited",
    desc: "30–50 page pack with 3–5yr projections, scenario models, exit letters and SEC-ready templates (409A / Reg D references).",
    sample: "/samples/sample-institutional.pdf" },
  { icon: Eye, name: "Free Ballpark Estimate", tiers: "Free",
    desc: "A single-page ballpark valuation range so you can see the platform in action before upgrading.",
    sample: "/samples/sample-free-ballpark.pdf" },
  { icon: Mail, name: "Investor Letter", tiers: "Professional +",
    desc: "A concise, narrative cover letter framing your valuation for investors — generated from your numbers, editable before you share." },
  { icon: LineChart, name: "3–5 Year Projections & Infographics", tiers: "Institutional +",
    desc: "Forward ARR, burn and runway projections with clean infographics for your deck and data room." },
  { icon: Handshake, name: "Exit & Scenario Letters", tiers: "Institutional +",
    desc: "Base / upside / conservative exit scenarios with an exit-path narrative for strategic and growth-equity buyers." },
  { icon: Table2, name: "Cap Table & Dilution View", tiers: "Institutional +",
    desc: "See how new money, option pools and preferences affect founder ownership across rounds." },
  { icon: Scale, name: "SEC-Ready Templates (409A / Reg D)", tiers: "Institutional +",
    desc: "Reference templates and methodology notes aligned to 409A safe-harbor and Reg D expectations. Not a substitute for an independent 409A appraisal." },
  { icon: FolderLock, name: "Secure Dealroom / VDR", tiers: "All paid tiers",
    desc: "Watermarked, access-controlled document vault with password-protected share links and an audit trail." },
];

const WHY = [
  { icon: Target, title: "Raise on your terms", body: "Walking into a raise with a defensible, multi-method valuation stops investors from anchoring you low. Founders who quantify their worth negotiate better pre-money terms." },
  { icon: Scale, title: "Stay compliant", body: "Option grants and equity comp need a fair-market-value basis. Our methodology references 409A safe-harbor practice so your paperwork holds up in diligence." },
  { icon: TrendingUp, title: "Track enterprise value over time", body: "Value isn't a one-time event. Re-run as ARR grows to see how each milestone moves your number — and to time your next round." },
  { icon: Handshake, title: "Speed up diligence", body: "A clean report, certificate and data room shortens the distance from first meeting to term sheet. Our median is 48 hours." },
];

const WHY_LINKS = [
  { label: "Startup valuation methods — what to use and avoid (Equidam)", url: "https://www.equidam.com/startup-valuation-methods-to-use-and-avoid/" },
  { label: "What is a 409A valuation? (Carta)", url: "https://carta.com/learn/startups/equity-management/409a-valuation/" },
  { label: "409A basics & safe harbor (Turley Law)", url: "https://turleylaw.com/blog/409a-basics" },
  { label: "Pre-money vs post-money valuation explained", url: "https://www.youtube.com/watch?v=jcX2EAdnlbw" },
];

const VIDEOS = [
  { id: "ynFT3RFIf4w", title: "The Venture Capital (VC) Method, explained", cat: "Valuation methods", src: "YouTube",
    why: "The exact math investors use to back into your pre-money from a target exit and return." },
  { id: "MFzHSMe5aIM", title: "Mastering Startup Valuation with DCF", cat: "Valuation methods", src: "YouTube",
    why: "How discounted cash flow works for later-stage startups, incl. WACC and illiquidity discounts." },
  { id: "jcX2EAdnlbw", title: "Why investors value startups differently than founders", cat: "Investor mindset", src: "YouTube",
    why: "A reality check on how diligence and risk shape the number you actually get." },
  { id: "LCCXSS2ubn8", title: "Term sheet red flags to push back on", cat: "Term sheets", src: "YouTube",
    why: "Liquidation preferences, participation and full-ratchet anti-dilution — and why they matter." },
  { id: "wQ9KHm9A0R4", title: "Cap table & dilution, broken down", cat: "Cap table", src: "YouTube",
    why: "A practical, spreadsheet-based walkthrough of how equity and dilution really work." },
  { id: "3jIzWHScIVQ", title: "How to raise capital: SAFEs, notes & priced rounds", cat: "Fundraising", src: "YouTube",
    why: "The instruments behind early rounds and how each affects your ownership." },
];

const FAQS = [
  { q: "How accurate is a ValueStackHQ valuation?", a: "Every valuation triangulates five independent methods (DCF, First Chicago, VC Method, Scorecard and comparables) and reports a range with a midpoint. The average spread between methods is around ±8%. It's built to be defensible in front of investors — but it is guidance for fundraising, not a substitute for a formal, independent 409A appraisal." },
  { q: "Which methods do you use?", a: "Discounted Cash Flow (DCF), the First Chicago method, the Venture Capital (VC) method, the Scorecard method, and public/private comparables. Each is weighted and shown transparently in your report and certificate." },
  { q: "Is the free valuation really free?", a: "Yes — every verified entity gets one free single-page ballpark estimate, no credit card required. Upgrade to Professional, Institutional or Unlimited when you need the full report, certificate and documents." },
  { q: "Why do I need to complete KYC before a valuation?", a: "Identity verification (via Stripe Identity) keeps the platform trustworthy for investors relying on our certificates, and prevents multi-account abuse of the free tier. It takes a couple of minutes with a government ID." },
  { q: "Can I verify a certificate someone sent me?", a: "Yes. Every certificate has a unique ID (e.g. VSHQ-9BD4F6). Go to the Verify page, enter the ID, and you'll see the authentic valuation details and range straight from our records." },
  { q: "What's the difference between your valuation and a 409A?", a: "A fundraising/VC valuation is forward-looking and reflects preferred-stock pricing; a 409A is a conservative fair-market-value appraisal of common stock for tax compliance. Our Institutional pack provides 409A-aligned methodology and templates, but a filed 409A requires an independent appraiser." },
  { q: "Can I share reports securely?", a: "Yes. Paid tiers include a watermarked, access-controlled dealroom with password-protected share links and an audit trail, so you control exactly who sees what." },
  { q: "What does the Unlimited plan include?", a: "$99/mo billed yearly ($1,188/year) for unlimited valuations across all your companies, every deliverable in Professional and Institutional, a portfolio dashboard with auto-recalculation, white-label outputs and priority support." },
  { q: "Do you store my financial documents?", a: "Uploaded documents are used to power your valuation and are kept in your account's encrypted store. You can delete them at any time. We never sell your data." },
  { q: "How long does a full report take?", a: "Minutes. Once your inputs are in, the engine runs all five methods and assembles your report, certificate and documents on the spot — no weeks of consultant back-and-forth." },
];

/* --------------------------------- page --------------------------------- */
export default function ResourcesPage() {
  const { section } = useParams();
  const nav = useNavigate();
  const active = SECTIONS.find((s) => s.id === section) ? section : "documents";

  return (
    <div className="bg-[#F8FAFC] min-h-screen"><TopNav />
      {/* header band */}
      <div className="relative overflow-hidden bg-[#0F2340] text-white">
        <div className="hero-glow-orb w-[380px] h-[380px] bg-[#F5C86B]/20 -top-32 right-10" />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 py-12">
          <span className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.16em] text-[#F5C86B] bg-white/10 border border-white/15 rounded-full px-3 py-1.5"><BookOpen size={13} /> RESOURCES</span>
          <h1 className="font-display text-[34px] sm:text-[44px] font-bold tracking-tight mt-4 max-w-[720px]">Everything you get — and everything you need to know<span className="text-[#EEA415]">.</span></h1>
          <p className="text-white/70 mt-3 text-[16px] max-w-[620px]">Explore sample documents, learn why valuation matters, watch curated guides, and get answers to the most common questions.</p>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8">
        {/* side nav */}
        <aside className="lg:sticky lg:top-[110px] self-start">
          <nav className="flex lg:flex-col gap-1.5 overflow-x-auto pb-1" data-testid="resources-nav">
            {SECTIONS.map((s) => {
              const Icon = s.icon; const on = active === s.id;
              return (
                <button key={s.id} onClick={() => nav(`/resources/${s.id}`)} data-testid={`resources-tab-${s.id}`}
                  className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg text-[14px] font-medium whitespace-nowrap transition-colors ${on ? "bg-[#174C82] text-white shadow-[0_8px_20px_rgba(23,76,130,.25)]" : "text-slate-600 hover:bg-slate-100"}`}>
                  <Icon size={17} /> {s.label}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* content */}
        <main className="min-w-0">
          {active === "documents" && (
            <section data-testid="resources-documents">
              <h2 className="font-display text-[26px] font-bold">The documents we produce</h2>
              <p className="text-slate-500 text-[15px] mt-1 max-w-[640px]">Download watermarked samples to see the quality for yourself. Samples use illustrative data.</p>
              <div className="grid sm:grid-cols-2 gap-4 mt-6">
                {DOCS.map((d) => {
                  const Icon = d.icon;
                  return (
                    <div key={d.name} className="card-vs p-5 flex flex-col" data-testid={`doc-card-${d.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                      <div className="flex items-start gap-3">
                        <span className="w-11 h-11 rounded-xl bg-[#FFF8EB] flex items-center justify-center shrink-0"><Icon size={20} className="text-[#174C82]" /></span>
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-900 text-[15px]">{d.name}</div>
                          <div className="text-[11px] font-medium text-[#EEA415] tracking-wide mt-0.5">{d.tiers}</div>
                        </div>
                      </div>
                      <p className="text-[13px] text-slate-500 mt-3 leading-relaxed flex-1">{d.desc}</p>
                      {d.sample ? (
                        <div className="flex gap-2 mt-4">
                          <a href={d.sample} target="_blank" rel="noopener noreferrer" className="btn-navy-vs h-9 px-3 text-[13px] font-semibold flex items-center gap-1.5 flex-1 justify-center" data-testid="doc-view-sample"><Eye size={15} /> View sample</a>
                          <a href={d.sample} download className="btn-primary-vs h-9 px-3 text-[13px] font-semibold flex items-center gap-1.5 flex-1 justify-center" data-testid="doc-download-sample"><Download size={15} /> Download</a>
                        </div>
                      ) : (
                        <div className="mt-4 flex items-center gap-1.5 text-[12px] font-medium text-emerald-700"><CheckCircle2 size={14} /> Included in your report</div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="card-vs p-5 mt-5 flex flex-col sm:flex-row items-center justify-between gap-4 bg-[#FFF8EB] border-[#F5C86B]">
                <div><div className="font-semibold text-slate-900">Want these for your own company?</div><p className="text-[13px] text-slate-600">Start with a free ballpark, then unlock the full suite.</p></div>
                <Link to="/pricing" className="btn-primary-vs h-11 px-6 font-semibold flex items-center gap-2 whitespace-nowrap" data-testid="documents-cta">See pricing <ArrowRight size={16} /></Link>
              </div>
            </section>
          )}

          {active === "why" && (
            <section data-testid="resources-why">
              <h2 className="font-display text-[26px] font-bold">Why valuation matters</h2>
              <p className="text-slate-500 text-[15px] mt-1 max-w-[640px]">A defensible valuation is leverage. Here's what it changes for you.</p>
              <div className="grid sm:grid-cols-2 gap-4 mt-6">
                {WHY.map((w) => { const Icon = w.icon; return (
                  <div key={w.title} className="card-vs p-5">
                    <span className="w-11 h-11 rounded-xl bg-[#174C82] flex items-center justify-center"><Icon size={20} className="text-[#F5C86B]" /></span>
                    <div className="font-semibold text-slate-900 text-[16px] mt-3">{w.title}</div>
                    <p className="text-[13px] text-slate-500 mt-1.5 leading-relaxed">{w.body}</p>
                  </div>
                ); })}
              </div>
              <div className="card-vs p-5 mt-5">
                <div className="font-semibold text-slate-900 flex items-center gap-2"><ExternalLink size={16} className="text-[#174C82]" /> Related reading & references</div>
                <ul className="mt-3 space-y-2">
                  {WHY_LINKS.map((l) => (
                    <li key={l.url}><a href={l.url} target="_blank" rel="noopener noreferrer" className="text-[14px] text-[#174C82] hover:underline flex items-center gap-1.5" data-testid="why-link"><ArrowRight size={14} className="shrink-0" /> {l.label}</a></li>
                  ))}
                </ul>
                <p className="text-[11px] text-slate-400 mt-3">External links are provided for education and open on third-party sites. ValueStackHQ is not affiliated with these publishers.</p>
              </div>
            </section>
          )}

          {active === "videos" && (
            <section data-testid="resources-videos">
              <h2 className="font-display text-[26px] font-bold">Video guides</h2>
              <p className="text-slate-500 text-[15px] mt-1 max-w-[640px]">Hand-picked explainers from around the web. Click any card to watch.</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
                {VIDEOS.map((v) => (
                  <a key={v.id} href={`https://www.youtube.com/watch?v=${v.id}`} target="_blank" rel="noopener noreferrer" className="card-vs p-0 overflow-hidden text-left group block" data-testid={`video-card-${v.id}`}>
                    <div className="relative aspect-video bg-slate-200 overflow-hidden">
                      <img src={`https://img.youtube.com/vi/${v.id}/hqdefault.jpg`} alt={v.title} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" />
                      <span className="absolute inset-0 flex items-center justify-center bg-slate-900/25 group-hover:bg-slate-900/10 transition-colors">
                        <span className="w-12 h-12 rounded-full bg-white/95 flex items-center justify-center shadow-lg"><Play size={20} className="text-[#174C82] ml-0.5" fill="currentColor" /></span>
                      </span>
                      <span className="absolute top-2 left-2 text-[10px] font-bold tracking-wide text-white bg-[#174C82]/90 rounded-full px-2 py-0.5">{v.cat}</span>
                    </div>
                    <div className="p-4">
                      <div className="font-semibold text-slate-900 text-[14px] leading-snug group-hover:text-[#174C82] transition-colors">{v.title}</div>
                      <p className="text-[12px] text-slate-500 mt-1">{v.why}</p>
                      <div className="text-[11px] text-slate-400 mt-2 flex items-center gap-1"><Video size={12} /> Watch on {v.src} <ExternalLink size={11} /></div>
                    </div>
                  </a>
                ))}
              </div>
            </section>
          )}

          {active === "faq" && (
            <section data-testid="resources-faq">
              <h2 className="font-display text-[26px] font-bold">Frequently asked questions</h2>
              <p className="text-slate-500 text-[15px] mt-1">Can't find an answer? <Link to="/contact" className="text-[#174C82] font-semibold">Contact us</Link>.</p>
              <div className="card-vs p-2 sm:p-4 mt-6">
                <Accordion type="single" collapsible className="w-full">
                  {FAQS.map((f, i) => (
                    <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`}>
                      <AccordionTrigger className="text-left text-[15px] font-semibold text-slate-900 hover:no-underline px-2">{f.q}</AccordionTrigger>
                      <AccordionContent className="text-[14px] text-slate-600 leading-relaxed px-2">{f.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </section>
          )}
        </main>
      </div>

      <SiteFooter />
    </div>
  );
}
